import {TRACKING_FIELDS, getTrackingData, updateTrackingData} from '../../scripts/common.js';

export function setupFormTracking(survey, onUpdate = () => {}) {
  function normalizeTrackingValue(value) {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = String(value).trim();
    return normalized || null;
  }

  function applyTrackingDataToSurvey(trackingData, rewriteExisting = true) {
    TRACKING_FIELDS.forEach((fieldName) => {
      const incomingValue = normalizeTrackingValue(trackingData?.[fieldName]);
      if (incomingValue === null) {
        return;
      }
      const currentValue = normalizeTrackingValue(survey.getValue(fieldName));
      if (rewriteExisting || currentValue === null) {
        survey.setValue(fieldName, incomingValue);
      }
    });
  }

  function fillMissingTrackingDataFromStorage() {
    applyTrackingDataToSurvey(getTrackingData(), false);
  }

  function collectTrackingPayloadFromSurvey() {
    const trackingData = getTrackingData();
    const payload = {};

    TRACKING_FIELDS.forEach((fieldName) => {
      const fromSurvey = normalizeTrackingValue(survey.getValue(fieldName));
      const fromStorage = normalizeTrackingValue(trackingData[fieldName]);
      const finalValue = fromSurvey ?? fromStorage;
      payload[fieldName] = finalValue;
      if (finalValue !== null) {
        survey.setValue(fieldName, finalValue);
      }
    });
    return payload;
  }

  function hasAnyTrackingData(trackingData) {
    return TRACKING_FIELDS.some((fieldName) => normalizeTrackingValue(trackingData?.[fieldName]) !== null);
  }

  const pendingTrackingResolvers = new Set();

  const handleTrackingMessage = (event) => {
    const payload = event?.data;
    if (!payload || payload.type !== 'utm' || typeof payload !== 'object') {
      return;
    }
    const mergedTrackingData = updateTrackingData(payload.data || {});
    applyTrackingDataToSurvey(mergedTrackingData, true);
    pendingTrackingResolvers.forEach((resolver) => resolver(true));
    pendingTrackingResolvers.clear();
    onUpdate();
  };

  window.addEventListener('message', handleTrackingMessage);
  fillMissingTrackingDataFromStorage();

  function requestTrackingFromParent(timeoutMs = 700) {
    if (window.parent === window) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      let settled = false;
      let timerId = null;

      const resolver = (received) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timerId !== null) {
          window.clearTimeout(timerId);
        }
        pendingTrackingResolvers.delete(resolver);
        resolve(received);
      };

      pendingTrackingResolvers.add(resolver);
      timerId = window.setTimeout(() => resolver(false), timeoutMs);

      try {
        window.parent.postMessage({type: 'tlf-tracking-request'}, '*');
      } catch (error) {
        resolver(false);
      }
    });
  }
  async function collectPayload() {
    fillMissingTrackingDataFromStorage();
    let payload = collectTrackingPayloadFromSurvey();
    if (!hasAnyTrackingData(payload)) {
      await requestTrackingFromParent();
      fillMissingTrackingDataFromStorage();
      payload = collectTrackingPayloadFromSurvey();
    }
    return payload;
  }

  function dispose() {
    window.removeEventListener('message', handleTrackingMessage);
    pendingTrackingResolvers.forEach(resolve => resolve(false));
    pendingTrackingResolvers.clear();
  }

  return {fillMissingTrackingDataFromStorage, collectPayload, dispose};
}
